<script setup lang="ts">
/**
 * @author Beka Chkhaidze
 */
import { defineAsyncComponent, computed, ref, markRaw } from "vue";
import enGB from "date-fns/locale/en-GB/index";
import ka from "date-fns/locale/ka/index";
import ru from "date-fns/locale/ru/index";
import { format } from "date-fns";
import { hasData } from "./ts/util";

import type { Component } from "vue";
import type { CurrentContentInterface, SecondaryDataInterface } from "@/types/ContentType";
import type { ComplexComponentItem } from "./ts/Content.interface";
const Picture = defineAsyncComponent(
  () => import("@/components/regularComponents/UIComponents/Picture.vue")
);


import { useMainStore } from "@/store/main/index";
import LeftArrow from "@/components/regularComponents/UIComponents/SVG/chevrons/LeftArrow.vue";


const mainStore = useMainStore();
const currentContentData = useState<CurrentContentInterface | null>("currentContent");

const Title = defineAsyncComponent(() => import("@/components/regularComponents/UIComponents/Title.vue"));
const Wrapper = defineAsyncComponent(() => import("./parts/Wrapper.vue"));

const props = defineProps<{
  contentData: SecondaryDataInterface | null;
}>();

const complexComponents = ref<
  {
    component: Component;
    item: ComplexComponentItem;
  }[]
>([]);

const router = useRouter();


const hasUnset = computed(() => {
  return props.contentData?.conf?.conf.includes("unset");
});

const components = computed(
  () =>
    props.contentData?.data?.list?.[0]?.content ||
    currentContentData.value?.primary?.data?.list?.[0]?.content
);

const init = () => {
  if (!components.value) return;

  components.value?.forEach((item) => {
    const name = item.conf?.type;
    if (!name) return;

    const capitalizedName = name.charAt(0).toUpperCase() + name.slice(1);

    const complexComponent = markRaw(
      defineAsyncComponent(() =>
        import(`../../ComplexComponent/${capitalizedName}.vue`).catch((e) => {
          console.warn("[ ComplexComponent ]", e.message);
        })
      )
    );

    complexComponents.value.push({
      component: complexComponent,
      item,
    });
  });
};


const path = computed(() => router.currentRoute.value.params?.["path1"] || "");
const dataList = computed(() => currentContentData.value?.primary?.data?.list?.[0]);

const getNewsList = () => {
  router.push({ path: `/${mainStore.lang}/news` });
};

const formattedDate = (date: string) => {
  return format(new Date(date.split(" ")[0]), "dd MMMM yyyy", {
    locale: mainStore.lang === "ka" ? ka : mainStore.lang === "en" ? enGB : ru,
  });
};

watch(components, (ns) => {
  if (!ns) return;
  init();
}, {
  immediate: true,
  deep: true,
})
</script>

<template>
  <section :class="`complex-components ${path ? `path--${path}` : ''}`">
    <div class="container complex-components__main d-flex" v-if="complexComponents?.length">
      <!-- <SideNavigation /> -->

      <div class="complex-components__cont">
        <div class="complex-components__cont-right d-flex dir-col">
          <Title v-if="contentData?.data?.list?.[0]?.title" :title="contentData.data.list[0].title" />
          <div class="title-wrapper">
            <div class="title-content">
              <div v-if="dataList?.date" class="title-wrapper__date p-16">
                {{ formattedDate(dataList?.date) }}
              </div>

              <Title v-if="dataList?.title" :title="dataList?.title" />

              <p v-if="dataList?.teaser">
                {{ dataList?.teaser }}
              </p>
            </div>
            <div class="go-back" @click="getNewsList">
              <div class="go-back-wrapper">
                <LeftArrow class="go-back__icon" />
                <span class="back-btn l-12"> {{ $t("back") }}</span>
              </div>
            </div>
          </div>

          <div class="complex-components-image-cover">
            <Picture v-if="dataList?.cover?.[0]" class="complex-image" :devices="dataList?.cover?.[0]?.devices"
              :alt="dataList?.title" />
          </div>

          <template v-for="(component, index) in complexComponents" :key="index">
            <Wrapper :conf="component?.item?.conf" v-if="hasData(component.item.data)">
              <div :class="`complex-component__item ${index > 1 ? 'small' : ''}`">
                <component :is="component.component" :content="component.item" />
              </div>
            </Wrapper>
          </template>
        </div>

        <!-- <div class="complex-components-image-cover">
          <Picture
            v-if="dataList?.cover?.[0]"
            class="complex-image"
            :devices="dataList?.cover?.[0].devices"
          />
        </div> -->
        <!-- 
        <template v-for="(component, index) in complexComponents" :key="index">
          <Wrapper :conf="component?.item?.conf" v-if="hasData(component.item.data)">
            <div
              :class="[
                'complex-component__item',
                { small: index > 1 },
                { unset: hasUnset },
                { wc: component.item.conf.type != 'banner' },
              ]"
            >
              <component :is="component.component" :content="component.item" />
            </div>
          </Wrapper>
        </template> -->
      </div>
    </div>
  </section>
</template>

<style src="./styles/complexComponents.scss"></style>
